import { CompareModeButton } from "../CompareModeButton";
import { lazy, Suspense, useState } from "react";
import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { ProductCard } from "../ProductCard";
import { Pagination, SkeletonGrid } from "../Paging";
import { FilterPanel, AppliedChips, QuickChips, EmptyState, appliedChips, SORTS, SIZES } from "./FilterPanel";

const MobileFilters = lazy(() => import("./MobileFilters"));  // radix sheet loads on first open
const PILL = "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border border-[var(--brand-primary)] px-3 text-[13px] font-bold text-[var(--brand-primary)]";

// T75: phones get ONE clean row: [Filters (n)] [Sort] [Compare] ... count. Filters + sort live in one bottom sheet. md+ unchanged.
function Toolbar({ data, L, prefix, onFilters, nFilters }) {
  const sort = L.sp.get("sort") || "relevance";
  const size = Number(L.sp.get("size") || 24);
  return (
    <div className="sticky top-[116px] md:top-[72px] z-20 -mx-5 mb-3 flex flex-nowrap items-center gap-1.5 border-b border-slate-200 bg-white/95 px-3 py-2.5 backdrop-blur-md md:mx-0 md:mb-4 md:flex-wrap md:gap-3 md:rounded-xl md:border md:px-4 md:py-3" data-testid="results-toolbar">
      <button type="button" onClick={() => onFilters("filters")} data-testid="mobile-filters-open" className={`${PILL} md:h-auto md:px-4 md:py-2 md:text-sm lg:hidden`}>
        <SlidersHorizontal size={15} aria-hidden="true" /><span>{nFilters ? `Filters (${nFilters})` : "Filters"}</span>
      </button>
      <button type="button" onClick={() => onFilters("sort")} data-testid="mobile-sort-open" aria-label={`Sort: ${(SORTS.find(([v]) => v === sort) || SORTS[0])[1]}`}
        className={`${PILL} border-slate-300 text-slate-700 md:hidden`}>Sort<ChevronDown size={15} aria-hidden="true" /></button>
      <div className="order-last ml-auto shrink-0 whitespace-nowrap text-xs text-slate-600 md:order-none md:ml-0 md:text-sm" aria-live="polite" data-testid={`${prefix}-count`}>
        <b className="text-[var(--brand-primary)] tabular-nums" data-testid="results-count">{(data.total || 0).toLocaleString("en-AU")}</b> <span className="max-[419px]:hidden">products</span>
      </div>
      {(data.total || 0) >= 2 && <CompareModeButton prefix={prefix} />}
      <div className="ml-auto hidden items-center gap-2 md:flex">
        <label className="hidden text-xs font-semibold text-slate-500 sm:block" htmlFor={`${prefix}-sort`}>Sort</label>
        <select id={`${prefix}-sort`} data-testid={`${prefix}-sort`} value={sort} onChange={(e) => L.set("sort", e.target.value === "relevance" ? null : e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-focus)]" aria-label="Sort products">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid={`${prefix}-size`} value={size} onChange={(e) => L.set("size", e.target.value === "24" ? null : e.target.value)}
          className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-focus)]" aria-label="Products per page">
          {SIZES.map((s) => <option key={s} value={s}>{`${s} / page`}</option>)}
        </select>
      </div>
    </div>
  );
}

// T54: loading placeholders built from the SAME classes / controls as Toolbar + QuickChips, so results replace them in place
const BAR = "sticky top-[116px] md:top-[72px] z-20 -mx-5 mb-3 flex flex-nowrap items-center gap-1.5 border-b border-slate-200 bg-white/95 px-3 py-2.5 backdrop-blur-md md:mx-0 md:mb-4 md:flex-wrap md:gap-3 md:rounded-xl md:border md:px-4 md:py-3";
const SEL = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const LoadingBars = ({ prefix }) => (
  <div aria-hidden="true" data-testid="listing-toolbar-skeleton">
    <div className={BAR}>
      <button type="button" disabled tabIndex={-1} data-tap className={`${PILL} border-slate-300 text-slate-400 md:h-auto md:px-4 md:py-2 md:text-sm lg:hidden`}><SlidersHorizontal size={15} /><span>Filters</span></button>
      <button type="button" disabled tabIndex={-1} data-tap className={`${PILL} border-slate-300 text-slate-400 md:hidden`}>Sort<ChevronDown size={15} /></button>
      <div className="order-last ml-auto text-xs text-slate-400 md:order-none md:ml-0 md:text-sm">Loading…</div>
      <div className="ml-auto hidden items-center gap-2 md:flex">
        <span className="hidden text-xs font-semibold text-slate-400 sm:block">Sort</span>
        <select disabled tabIndex={-1} data-tap={prefix === "category" || undefined} className={SEL}><option>Best match</option></select>
        <select disabled tabIndex={-1} data-tap={prefix === "category" || undefined} className={`${SEL} px-2`}><option>24 / page</option></select>
      </div>
    </div>
    <div className="mb-4 space-y-3"><div className="-mx-1 flex gap-2 overflow-hidden px-1 pb-1">
      {["In stock 000", "Brand 000", "Brand 000", "Under $100 00", "$100–$300 00"].map((t, i) => (
        <button key={i} type="button" disabled tabIndex={-1} data-tap className="shrink-0 inline-flex animate-pulse items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-3.5 py-1.5 text-[13px] font-semibold text-transparent motion-reduce:animate-none">{t}</button>))}
    </div></div>
  </div>
);

// T12.c: the ONE listing layout (All Products, category, brand, search). URL holds all state.
export function ListingView({ L, prefix, title, crumbs, lead, hideBrand, baseLabel, emptyBody, notice }) {
  const [sheet, setSheet] = useState(false);
  const { data, loading } = L;
  if (!data) return (
    <div data-testid={`${prefix}-loading`}>
      {crumbs}
      <h1 className="rs-h1 mb-4 text-[var(--brand-primary)]" aria-hidden={!title}>{title || "\u00a0"}</h1>
      {lead}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[264px_1fr]"><div className="hidden lg:block" />
        <div className="min-w-0"><LoadingBars prefix={prefix} /><SkeletonGrid n={6} /></div></div></div>);
  const chips = appliedChips(data, L);
  return (
    <>
      {crumbs}
      <h1 className="rs-h1 mb-4 text-[var(--brand-primary)]" data-testid={`${prefix}-title`}>{title}</h1>
      {lead}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[264px_1fr]">
        <aside className="hidden lg:block" aria-label="Filters">
          <div className="sticky top-[88px] max-h-[calc(100vh-104px)] overflow-y-auto pr-2 [scrollbar-width:thin]">
            <FilterPanel data={data} L={L} hideBrand={hideBrand} baseLabel={baseLabel} />
          </div>
        </aside>
        <div className="min-w-0">
          <Toolbar data={data} L={L} prefix={prefix} onFilters={(f) => setSheet(f || "filters")} nFilters={chips.length} />
          <div className="mb-4 space-y-3">
            <QuickChips data={data} L={L} hideBrand={hideBrand} />
            <AppliedChips chips={chips} onClear={L.clearAll} />
          </div>
          <div className={`transition-opacity duration-200 ${loading ? "opacity-60" : ""}`} aria-busy={loading}>
            {data.items.length === 0 ? (
              <EmptyState data={data} chips={chips} L={L} prefix={prefix}>{emptyBody}</EmptyState>
            ) : (
              <>
                {notice?.(data)}
                <div className="grid grid-cols-2 gap-4 xl:grid-cols-3" data-testid={`${prefix}-grid`}>
                  {data.items.map((p, i) => <ProductCard key={p.id} p={p} priority={i < 2} />)}
                </div>
                <Pagination page={data.page} totalPages={data.total_pages} total={data.total || 0} testid={`${prefix}-pagination`} />
              </>
            )}
          </div>
        </div>
      </div>
      {sheet && <Suspense fallback={null}><MobileFilters open={!!sheet} focus={sheet} setOpen={setSheet} data={data} L={L} chips={chips} hideBrand={hideBrand} baseLabel={baseLabel} loading={loading} /></Suspense>}
    </>
  );
}
